import React, { useEffect, useMemo, useState } from 'react';
import { X, Shield, Check, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { AdminAccount, GameProvider } from '../../../types';
import { useGameProviders, useAdminPermissions, useSetPermissions } from '../../../hooks/useApi';
import {
  ROLE_PERMISSIONS,
  getAdminRoleLabel,
  roleUsesGameProviders,
  toBackendPermissionCodes,
} from '../../../config/permissions';
import RolePermissionSummary from './RolePermissionSummary';

interface ManagePermissionsModalProps {
  admin: AdminAccount | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Permissions view for an admin. KIOSK-720 makes the permission set fixed by role, so that part
 * is read-only (just shows proper names) — role changes happen in the Manage Admin modal.
 * The ONE thing system_admin can set here is which game providers a game-manager role
 * (finance_admin/operation_admin) is scoped to — that assignment has no other UI, so this
 * popup keeps the same editable checkbox picker used in Create Admin.
 *
 * managedGameProviders is stored per-permission on the AdminPermission join row (not on the
 * admin object itself), so it must be fetched via useAdminPermissions rather than read off
 * `admin` — the list endpoint never returns it.
 */
const ManagePermissionsModal: React.FC<ManagePermissionsModalProps> = ({
  admin,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { data: gameProvidersData } = useGameProviders();
  const { data: adminPermissionsData, isLoading: loadingPermissions } = useAdminPermissions(
    admin?.id || ''
  );
  const setPermissions = useSetPermissions();

  const [selectedGameProviders, setSelectedGameProviders] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const gameProviders = useMemo(
    () => gameProvidersData?.data?.items || [],
    [gameProvidersData]
  );

  const currentManagedGameProviders = useMemo(() => {
    const permissions = adminPermissionsData?.data?.permissions || [];
    const codes = new Set<string>();
    permissions.forEach((p) => {
      if (Array.isArray(p.managedGameProviders)) {
        p.managedGameProviders.forEach((code) => codes.add(code));
      }
    });
    return Array.from(codes);
  }, [adminPermissionsData]);

  const needsGameProviders = admin ? roleUsesGameProviders(admin.role) : false;

  // Sync local editable selection from the server whenever the target admin or their saved
  // providers change (e.g. popup opened for a different admin, or after a successful save).
  useEffect(() => {
    setSelectedGameProviders(currentManagedGameProviders);
    setErrorMessage('');
  }, [admin?.id, currentManagedGameProviders]);

  const handleToggleGameProvider = (code: string) => {
    setSelectedGameProviders((prev) =>
      prev.includes(code) ? prev.filter((p) => p !== code) : [...prev, code]
    );
    setErrorMessage('');
  };

  const handleSave = async () => {
    if (!admin) return;

    if (needsGameProviders && selectedGameProviders.length === 0) {
      setErrorMessage('Please select at least one game provider to scope this admin\'s access.');
      return;
    }

    try {
      await setPermissions.mutateAsync({
        adminId: admin.id,
        data: {
          // Permissions are fixed by role — resend the role's codes so setPermissions (which
          // replaces the full set) doesn't drop anything while we update managedGameProviders.
          permissionCodes: toBackendPermissionCodes(ROLE_PERMISSIONS[admin.role] ?? []),
          managedGameProviders: needsGameProviders ? selectedGameProviders : [],
        },
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update managed game providers:', error);
      setErrorMessage('Failed to save. Please try again.');
    }
  };

  if (!isOpen || !admin) return null;

  const roleLabel = getAdminRoleLabel(admin.role);
  const isSubmitting = setPermissions.isLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg">Permissions - {admin.email}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {loadingPermissions ? (
            <div className="flex justify-center items-center h-32">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Role</label>
                <div className="flex items-center gap-2 p-3 border rounded-md bg-gray-50">
                  <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="font-medium">{roleLabel}</span>
                </div>
              </div>

              {/* Permissions granted by role — fixed by role, view only. */}
              <RolePermissionSummary role={admin.role} showGameProviders={false} />

              {/* Managed Game Providers — the one thing system_admin can actually set here. */}
              {needsGameProviders && (
                <div className="space-y-2 border-t pt-4">
                  <label className="text-sm font-medium">Managed Game Providers</label>
                  <p className="text-xs text-muted-foreground">
                    Select which game providers this admin can operate on (wallet adjustment,
                    transactions). At least one provider is required.
                  </p>
                  {errorMessage && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <p className="text-sm text-destructive">{errorMessage}</p>
                    </div>
                  )}
                  {gameProviders.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Loading game providers...</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                      {gameProviders.map((provider: GameProvider) => {
                        const isSelected = selectedGameProviders.includes(provider.code);
                        return (
                          <div
                            key={provider.id}
                            className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                              isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-accent/50'
                            }`}
                            onClick={() => handleToggleGameProvider(provider.code)}
                          >
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{provider.name}</span>
                                <code className="text-xs text-muted-foreground">
                                  ({provider.code})
                                </code>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {provider.currency} • Status: {provider.status}
                              </p>
                            </div>
                            {isSelected ? (
                              <Check className="h-5 w-5 text-primary flex-shrink-0" />
                            ) : (
                              <XCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {selectedGameProviders.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {selectedGameProviders.length} game provider(s) selected
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Close
                </Button>
                {needsGameProviders && (
                  <Button onClick={handleSave} disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManagePermissionsModal;
